import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { resetBlocksClipboard } from '@plone/volto/actions/blocksClipboard/blocksClipboard';
import { setUIState } from '@plone/volto/actions/form/form';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { Plug } from '@plone/volto/components/manage/Pluggable';
import { messages } from '@plone/volto/helpers/MessageLabels/MessageLabels';
import copySVG from '@plone/volto/icons/copy.svg';
import cutSVG from '@plone/volto/icons/cut.svg';
import pasteSVG from '@plone/volto/icons/paste.svg';
import trashSVG from '@plone/volto/icons/delete.svg';

import {
  createBlockClipboardPayload,
  cutBlockSelection,
  getBlockSelectionMediaUrls,
  parseBlockClipboardPayload,
  pasteBlockClipboardPayload,
  removeBlockSelection,
  resolveBlockClipboardTarget,
  serializeBlockClipboardPayload,
} from '../../../../../helpers/blockClipboard';
import {
  getLegacyMultiSelected,
  normalizeBlockSelection,
} from '../../../../../helpers/blockSelection';
import {
  clearBlockClipboardSession,
  selectBlockClipboardSession,
  setBlockClipboardSession,
} from '../../../../../reducers/blockClipboardSession';
import {
  selectHasPendingMediaUploads,
  stageMediaDeletion,
} from '../../../../../reducers/mediaUploads';

export function BlocksToolbarComponent({
  formData,
  selectedBlock,
  onChangeBlocks,
  onSelectBlock,
  selectionScopePath,
}) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const selection = useSelector(
    (state) => state.form?.ui?.blockSelection || null,
  );
  const focusedBlockRef = useSelector(
    (state) => state.form?.ui?.focusedBlockRef || null,
  );
  const serializedPayload = useSelector(selectBlockClipboardSession);
  const mediaUploadPending = useSelector(selectHasPendingMediaUploads);
  const pendingPasteSelectionRef = useRef(null);
  const normalizedSelection = useMemo(
    () => normalizeBlockSelection(formData, selection),
    [formData, selection],
  );
  const clipboardPayload = useMemo(
    () => parseBlockClipboardPayload(serializedPayload || ''),
    [serializedPayload],
  );
  const pasteTarget = useMemo(
    () =>
      resolveBlockClipboardTarget(
        formData,
        normalizedSelection,
        focusedBlockRef,
        selectedBlock,
      ),
    [formData, focusedBlockRef, normalizedSelection, selectedBlock],
  );

  useEffect(() => {
    dispatch(resetBlocksClipboard());
  }, [dispatch]);

  useEffect(() => {
    const clearSession = () => dispatch(clearBlockClipboardSession());
    const clearHiddenSession = () => {
      if (document.visibilityState === 'hidden') clearSession();
    };

    window.addEventListener('blur', clearSession);
    document.addEventListener('visibilitychange', clearHiddenSession);
    return () => {
      window.removeEventListener('blur', clearSession);
      document.removeEventListener('visibilitychange', clearHiddenSession);
    };
  }, [dispatch]);

  useEffect(() => {
    const pendingSelection = pendingPasteSelectionRef.current;
    const nextSelection = normalizeBlockSelection(formData, pendingSelection);
    if (!nextSelection) return;

    pendingPasteSelectionRef.current = null;
    const focusedRef = nextSelection.anchor;
    onSelectBlock?.(focusedRef.scopePath[0] ?? focusedRef.id);
    dispatch(
      setUIState({
        blockSelection: nextSelection,
        multiSelected: getLegacyMultiSelected(nextSelection),
        gridSelected: null,
        orderSelected: null,
        focusedBlockRef: focusedRef,
      }),
    );
  }, [dispatch, formData, onSelectBlock]);

  if (selectionScopePath?.length) return null;

  const clearSelection = (focusedRef) => {
    dispatch(
      setUIState({
        blockSelection: null,
        multiSelected: [],
        gridSelected: null,
        orderSelected: null,
        focusedBlockRef: focusedRef || null,
      }),
    );
    onSelectBlock?.(
      focusedRef ? focusedRef.scopePath[0] ?? focusedRef.id : null,
    );
  };

  const copyBlocks = () => {
    if (!normalizedSelection || mediaUploadPending) return;
    const serialized = serializeBlockClipboardPayload(
      createBlockClipboardPayload(formData, normalizedSelection),
    );
    if (!serialized) return;

    const focusedRef =
      normalizedSelection.items[normalizedSelection.items.length - 1];
    dispatch(setBlockClipboardSession(serialized));
    clearSelection(focusedRef);
  };

  const cutBlocks = () => {
    if (!normalizedSelection || mediaUploadPending) return;
    const result = cutBlockSelection(formData, normalizedSelection);
    const serialized = result
      ? serializeBlockClipboardPayload(result.payload)
      : null;
    if (!result || !serialized) return;

    getBlockSelectionMediaUrls(formData, normalizedSelection).forEach((url) =>
      dispatch(stageMediaDeletion(url)),
    );
    dispatch(setBlockClipboardSession(serialized));
    onChangeBlocks(result.properties);
    clearSelection(result.nextFocusRef);
  };

  const deleteBlocks = () => {
    if (!normalizedSelection) return;
    const result = removeBlockSelection(formData, normalizedSelection);
    if (!result) return;

    getBlockSelectionMediaUrls(formData, normalizedSelection).forEach((url) =>
      dispatch(stageMediaDeletion(url)),
    );
    onChangeBlocks(result.properties);
    clearSelection(result.nextFocusRef);
  };

  const pasteBlocks = () => {
    if (!clipboardPayload || !pasteTarget) return;
    const result = pasteBlockClipboardPayload(
      formData,
      clipboardPayload,
      pasteTarget,
      uuid,
    );
    if (!result) return;

    pendingPasteSelectionRef.current = result.selection;
    onChangeBlocks(result.properties);
  };

  const selectedCount = normalizedSelection?.items?.length || 0;
  const clipboardCount = clipboardPayload?.items?.length || 0;

  return (
    <>
      {selectedCount > 0 && (
        <>
          <Plug pluggable="main.toolbar.bottom" id="blocks-delete-btn">
            <button
              aria-label={intl.formatMessage(messages.deleteBlocks)}
              onClick={deleteBlocks}
              tabIndex={0}
              className="deleteBlocks"
              id="toolbar-delete-blocks"
            >
              <Icon name={trashSVG} size="30px" />
            </button>
          </Plug>
          <Plug pluggable="main.toolbar.bottom" id="blocks-cut-btn">
            <button
              aria-label={intl.formatMessage(messages.cutBlocks)}
              onClick={cutBlocks}
              tabIndex={0}
              className="cutBlocks"
              id="toolbar-cut-blocks"
              disabled={mediaUploadPending}
            >
              <Icon name={cutSVG} size="30px" />
            </button>
          </Plug>
          <Plug pluggable="main.toolbar.bottom" id="blocks-copy-btn">
            <button
              aria-label={intl.formatMessage(messages.copyBlocks)}
              onClick={copyBlocks}
              tabIndex={0}
              className="copyBlocks"
              id="toolbar-copy-blocks"
              disabled={mediaUploadPending}
            >
              <Icon name={copySVG} size="30px" />
            </button>
          </Plug>
        </>
      )}
      {clipboardCount > 0 && pasteTarget && (
        <Plug
          pluggable="main.toolbar.bottom"
          id="block-paste-btn"
          dependencies={[pasteTarget.scopePath.join('/'), pasteTarget.id]}
        >
          <button
            aria-label={intl.formatMessage(messages.pasteBlocks)}
            onClick={pasteBlocks}
            tabIndex={0}
            className="pasteBlocks"
            id="toolbar-paste-blocks"
          >
            <span className="blockCount">{clipboardCount}</span>
            <Icon name={pasteSVG} size="30px" />
          </button>
        </Plug>
      )}
    </>
  );
}

export default BlocksToolbarComponent;
